import { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mark, mergeAttributes } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import { Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { getMemoTableCSSVariables } from '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/extensions/MemoTable';
import { createMemoReadOnlyExtensions } from '@/layouts/03-Shared/routes/Memos/createMemoReadOnlyExtensions';
import { useResourcePreviewChanges } from '@/shared/components/resource-route-preview/changeContext';
import { ResourceVersionListItem } from '@/types';

import ChangeNavigator from './ChangeNavigator';
import { buildMemoVersionDiff } from './memoDiff';

import '@/layouts/02-Authenticated/layout/03-Content/routes/06-Memos/routes/MemoDetail/components/memo-content.css';

interface MemoVersionPreviewProps {
    embedded?: boolean;
    version: ResourceVersionListItem;
}

const MemoDiffAdded = Mark.create({
    name: 'memoDiffAdded',
    addAttributes() {
        return {
            changeId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-change-id'),
                renderHTML: (attributes) =>
                    typeof attributes.changeId === 'string' ? { 'data-change-id': attributes.changeId } : {},
            },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-memo-diff="added"]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 'data-memo-diff': 'added' }), 0];
    },
});

const MemoDiffRemoved = Mark.create({
    name: 'memoDiffRemoved',
    addAttributes() {
        return {
            changeId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-change-id'),
                renderHTML: (attributes) =>
                    typeof attributes.changeId === 'string' ? { 'data-change-id': attributes.changeId } : {},
            },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-memo-diff="removed"]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 'data-memo-diff': 'removed' }),
            0,
        ];
    },
});

const MemoVersionPreview: FC<MemoVersionPreviewProps> = ({ embedded = false, version }): JSX.Element => {
    const { resolvedThemeName, token } = useAntDesignTheme();
    const diff = useMemo(() => buildMemoVersionDiff(version), [version]);
    const [localHighlightChanges, setHighlightChanges] = useState(true);
    const preview = useResourcePreviewChanges();
    const highlightChanges =
        embedded && preview?.version.id === version.id ? preview.highlightChanges : localHighlightChanges;
    const [activeChangeIndex, setActiveChangeIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const extensions = useMemo(
        () =>
            createMemoReadOnlyExtensions({
                additionalExtensions: [MemoDiffAdded, MemoDiffRemoved],
                borderColor: token.colorBorder,
                dark: resolvedThemeName === 'dark',
            }),
        [resolvedThemeName, token.colorBorder],
    );
    const editor = useEditor(
        {
            editable: false,
            extensions,
            content: diff.html,
            editorProps: {
                attributes: {
                    'aria-label': 'Memo version with highlighted changes',
                    spellcheck: 'false',
                },
            },
            parseOptions: {
                preserveWhitespace: 'full',
            },
        },
        [extensions],
    );

    useEffect(() => {
        if (!editor || editor.getHTML() === diff.html) {
            return;
        }

        editor.commands.setContent(diff.html, { emitUpdate: false });
    }, [diff.html, editor]);

    const applyChangeStyles = useCallback(() => {
        const nodes = containerRef.current?.querySelectorAll<HTMLElement>('[data-memo-diff]');

        nodes?.forEach((node) => {
            node.tabIndex = -1;
            const added = node.dataset.memoDiff === 'added';

            node.style.backgroundColor = highlightChanges
                ? added
                    ? token.colorPrimaryBg
                    : token.colorFillSecondary
                : 'transparent';
            node.style.borderRadius = '0.1875rem';
            node.style.boxDecorationBreak = 'clone';
            node.style.border = 'none';
            node.style.boxShadow = 'none';
            node.style.color = highlightChanges ? (added ? token.colorPrimaryText : token.colorTextSecondary) : '';
            node.style.display = !highlightChanges && !added ? 'none' : 'inline';
            node.style.padding = highlightChanges ? '0.0625rem 0.125rem' : '0';
            node.style.textDecoration = highlightChanges && !added ? 'line-through' : 'none';
            node.style.setProperty('-webkit-box-decoration-break', 'clone');
        });
    }, [highlightChanges, token]);

    useEffect(() => {
        applyChangeStyles();
    }, [applyChangeStyles, diff.html, editor]);

    const focusChange = useCallback(
        (index: number) => {
            if (!diff.changes.length) {
                return;
            }

            const nextIndex = (index + diff.changes.length) % diff.changes.length;
            const nextChange = diff.changes[nextIndex];
            setHighlightChanges(true);
            setActiveChangeIndex(nextIndex);

            window.requestAnimationFrame(() => {
                containerRef.current
                    ?.querySelector<HTMLElement>(`[data-change-id="${nextChange.id}"]`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        },
        [diff.changes],
    );

    const title =
        typeof version.snapshot?.title === 'string' ? version.snapshot.title : (version.title ?? 'Untitled Memo');

    return (
        <div
            ref={containerRef}
            style={{
                maxWidth: embedded ? undefined : '68rem',
                minHeight: '100%',
                margin: '0 auto',
                width: embedded ? '100%' : undefined,
            }}
        >
            {!embedded && (
                <Typography.Title level={2} style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                    {title}
                </Typography.Title>
            )}
            <EditorContent
                editor={editor}
                style={
                    {
                        '--memo-filing-quote-text-color': token.colorTextSecondary,
                        '--memo-blockquote-border-color': token.colorBorder,
                        '--memo-filing-source-avatar-background': token.colorFillQuaternary,
                        '--memo-filing-source-avatar-border-color': token.colorBorderSecondary,
                        '--memo-filing-source-button-background': token.colorBgContainer,
                        '--memo-filing-source-button-border-color': token.colorBorder,
                        '--memo-filing-source-button-hover-color': token.colorPrimaryHover,
                        '--memo-filing-source-button-text-color': token.colorText,
                        '--memo-filing-source-chevron-color': token.colorTextTertiary,
                        '--memo-horizontal-rule-color': token.colorBorder,
                        '--memo-task-checkbox-background': token.colorBgContainer,
                        '--memo-task-checkbox-border-color': token.colorBorder,
                        '--memo-task-checkbox-checked-background': token.colorPrimary,
                        '--memo-task-checkbox-checked-border-color': token.colorPrimary,
                        '--memo-task-checkbox-check-color': token.colorTextLightSolid,
                        '--memo-task-checkbox-focus-color': token.colorPrimaryBorder,
                        ...getMemoTableCSSVariables(token),
                        color: token.colorTextBase,
                    } as CSSProperties
                }
            />
            {!embedded && diff.changes.length ? (
                <ChangeNavigator
                    activeChangeIndex={activeChangeIndex}
                    changes={diff.changes}
                    highlightChanges={highlightChanges}
                    onHighlightChangesChange={setHighlightChanges}
                    onSelectChange={focusChange}
                />
            ) : null}
        </div>
    );
};

export default MemoVersionPreview;
